import {Box, Button, Divider, Flex, Grid, Text, Title} from "@mantine/core";
import React, {useEffect, useState} from "react";
import axios from "axios";
import {getToken} from "../../auth/service/authService";
import {Organization} from "../../models/Organization";
import {jwtDecode} from "jwt-decode";
import {StorageLocation} from "../../models/StorageLocation";
import {RentContract} from "../../models/RentContract";
import {BackendUrl} from "../../util/config";
import {sumAllPastRevenue} from "../../ui/information/Customer/totalRevenue";
import {RevenueLast6MonthsChart} from "../../ui/information/Customer/RevenueLast6MonthChart";
import {DonutChart} from "@mantine/charts";
import {filterActiveRentContracts} from "../../Services/RentContractService";
import {ContractsBarChart} from "../../ui/information/Customer/ContractBarChart";
import {useNavigate} from "react-router-dom";

export const Overview = () => {
    const [organizations, setOrganizations] = useState<Organization[]>();
    const [storageLocations, setStorageLocations] = useState<StorageLocation[]>();
    const [rentContracts, setRentcontracts] = useState<RentContract[]>();
    const token = getToken();
    const navigate = useNavigate();

    useEffect(() => {
        fetchOrganization();
        fetchStorageLocations();
        fetchRentContracts();
    }, []);

    const fetchOrganization = () => {
        axios.get(BackendUrl + "/organizations", {
            headers: { Authorization: "Bearer " + getToken() }
        }).then(res => setOrganizations(res.data));
    };

    const fetchStorageLocations = () => {
        axios.get(BackendUrl + "/storage-locations", {
            headers: { Authorization: "Bearer " + getToken() }
        }).then(res => setStorageLocations(res.data));
    };

    const fetchRentContracts = () => {
        axios.get(BackendUrl + "/rent-contracts", {
            headers: { Authorization: "Bearer " + getToken() }
        }).then(res => setRentcontracts(res.data));
    };

    // Global stats
    const activeCount = filterActiveRentContracts(rentContracts!)?.length ?? 0;
    const totalLocs = storageLocations?.length ?? 0;

    return (

        <Grid gutter="md">
            {/* Header */}
            <Grid.Col span={12}>
                <Flex align="center" justify="space-between">
                    {token && <Text>Hallo {jwtDecode<{ sub: string }>(token).sub}</Text>}
                </Flex>
            </Grid.Col>

            <Grid.Col span={12}><Divider /></Grid.Col>

            {/* Overall Übersicht */}
            <Grid.Col span={12}>
                <Title order={2}>Übersicht über alle Betriebe</Title>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 3 }}>
                <Title order={4}>Gesamter Umsatz</Title>
                {rentContracts && <Title order={2}>{sumAllPastRevenue(rentContracts)} €</Title>}
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 3 }}>
                <Title order={4}>Anzahl Betriebe</Title>
                {organizations && <Title order={2}>{organizations.length}</Title>}
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 3 }}>
                <Title order={4}>Anzahl aller Stellplätze</Title>
                {storageLocations && <Title order={2}>{storageLocations.length}</Title>}
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 3 }}>
                <Title order={4}>Anzahl aller Mietverträge</Title>
                {rentContracts && <Title order={2}>{rentContracts.length}</Title>}
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 4 }}>
                <Title order={4} mb="xs">Umsatz der letzten 6 Monate</Title>
                {rentContracts && <RevenueLast6MonthsChart contracts={rentContracts} />}
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
                <Title order={4} mb="xs">Aktuelle Auslastung</Title>
                {rentContracts && (
                    <Flex justify="center">
                        <DonutChart
                            withLabelsLine
                            labelsType="value"
                            withLabels
                            data={[
                                { name: "Vermietet", value: activeCount, color: "green" },
                                { name: "Unvermietet", value: totalLocs - activeCount, color: "red" }
                            ]}
                        />
                    </Flex>
                )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
                <Title order={4} mb="xs">Beginn / Ende von Verträgen</Title>
                {rentContracts && <ContractsBarChart contracts={rentContracts} />}
            </Grid.Col>

            <Grid.Col span={12}><Divider /></Grid.Col>

            {/* Per-Organization Übersicht */}
            {organizations?.map(orga => {
                // Filter data for this organization
                const locsForOrga = storageLocations?.filter(loc => loc.organization?.organizationId === orga.id) ?? [];
                const contractsForOrga = rentContracts?.filter(rc => locsForOrga.some(loc => loc.id === rc.storageLocation.id)) ?? [];
                const activeCountOrg = filterActiveRentContracts(contractsForOrga)?.length ?? 0;

                return (
                    <React.Fragment key={orga.id}>
                        <Grid.Col span={12}>
                            <Flex direction={"row"} align={"center"} gap={"15px"}>
                                <Title order={2}>Übersicht über Betrieb: {orga.name}</Title>
                                <Button variant={"filled"} onClick={() => navigate(`/organizations/${orga.id}`)}>Bearbeiten</Button>
                            </Flex>
                        </Grid.Col>

                        <Grid.Col span={{ base: 12, sm: 3 }}>
                            <Title order={4}>Gesamter Umsatz</Title>
                            <Title order={2}>{sumAllPastRevenue(contractsForOrga)} €</Title>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 3 }}>
                            <Title order={4}>Anzahl Mitglieder</Title>
                            <Title order={2}>{orga.memberships.length}</Title>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 3 }}>
                            <Title order={4}>Anzahl Stellplätze</Title>
                            <Title order={2}>{locsForOrga.length}</Title>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 3 }}>
                            <Title order={4}>Anzahl Mietverträge</Title>
                            <Title order={2}>{contractsForOrga.length}</Title>
                        </Grid.Col>

                        <Grid.Col span={{ base: 12, md: 4 }}>
                            <Title order={4} mb="xs">Umsatz letzte 6 Monate</Title>
                            <RevenueLast6MonthsChart contracts={contractsForOrga} />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                            <Title order={4} mb="xs">Auslastung</Title>
                            <Flex justify="center">
                                <DonutChart
                                    withLabelsLine
                                    labelsType="value"
                                    withLabels
                                    data={[
                                        { name: "Vermietet", value: activeCountOrg, color: "green" },
                                        { name: "Unvermietet", value: locsForOrga.length - activeCountOrg, color: "red" }
                                    ]}
                                />
                            </Flex>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                            <Title order={4} mb="xs">Vertragszeitraum</Title>
                            <Box style={{ minWidth: 250, minHeight: 200 }}>
                                <ContractsBarChart contracts={contractsForOrga} />
                            </Box>
                        </Grid.Col>

                        <Grid.Col span={12}><Divider /></Grid.Col>
                    </React.Fragment>
                );
            })}
        </Grid>
    );
};
